/**
 * Flight-plan files read in the browser for a quote area (MB decision 124: "Teslyn does not make flight plans for
 * now: they are made outside Teslyn and imported"). Same rules as the Rust reader Teslyn Field uses
 * (`crates/teslyn-core/src/plan.rs`, `parse_sum` and `parse_bnd`), checked against the same F26048 files.
 *
 * - `*_fnl.sum`: header (zone, hemisphere, traverse and tie bearing and spacing, `Total Survey: <km> km`) and the line
 *   list after `Total Lines Count: n`, one `number x1 y1 x2 y2` row per line. A line is a tie when the second digit
 *   of its number is 9.
 * - `*_BND.inp`: zone, vertex count, `x, y` vertices, then traverse bearing and spacing.
 * - `*_LNS.lns` (the block's lines file, MB decision 169): a `// <block> Zone=<n> Hemi=<N|S>` header, then one
 *   `number x1 y1 x2 y2` row per line. It has no spacing or bearing header: they are measured from the lines (median
 *   bearing, median distance between neighbouring lines).
 */
import { parseLineNumber } from './lines.js';

export interface PlanFileLine {
  readonly number: number;
  readonly kind: 'traverse' | 'tie';
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly length_m: number;
}

/** A block's planned lines with their spacing and bearings: from a `.lns` lines file or a `.sum` summary. */
export interface LinesFile {
  readonly block_name: string;
  readonly zone: number | null;
  readonly hemisphere: 'N' | 'S' | null;
  readonly trav_bearing_deg: number;
  readonly trav_spacing_m: number;
  readonly tie_bearing_deg: number | null;
  readonly tie_spacing_m: number | null;
  readonly lines: readonly PlanFileLine[];
}

export interface SumFile extends LinesFile {
  readonly total_survey_km: number;
}

export interface BndFile {
  readonly zone: number | null;
  readonly boundary: ReadonlyArray<readonly [number, number]>;
  readonly trav_bearing_deg: number | null;
  readonly trav_spacing_m: number | null;
}

export class PlanFileError extends Error {
  override readonly name = 'PlanFileError';
}

function capture(text: string, re: RegExp): number | null {
  const m = re.exec(text);
  if (!m || m[1] === undefined) return null;
  const n = Number.parseFloat(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** Parse a `*_fnl.sum` flight-plan summary. Errors say what is missing, in plain words. */
export function parseSumFile(text: string): SumFile {
  const total = capture(text, /Total Survey:\s+([\d.]+) km/);
  if (total === null)
    throw new PlanFileError('This is not a flight-plan summary: "Total Survey: … km" is missing.');
  const travBearing = capture(text, /Trav Bearing:\s+([\d.]+)/);
  if (travBearing === null) throw new PlanFileError('The summary has no "Trav Bearing:".');
  const travSpacing = capture(text, /Trav Spacing:\s+([\d.]+) m/);
  if (travSpacing === null) throw new PlanFileError('The summary has no "Trav Spacing: … m".');
  const zone = capture(text, /^Zone:\s+(\d+)/m);
  const hemi = /^Hemi:\s+([NS])/m.exec(text)?.[1];
  const name = /^Block Name:\s*(.*?)\s*$/m.exec(text)?.[1] ?? '';
  const countMatch = /Total Lines Count:\s*(\d+)/.exec(text);
  if (!countMatch || countMatch[1] === undefined) {
    throw new PlanFileError('The summary has no line list ("Total Lines Count:").');
  }
  const count = Number.parseInt(countMatch[1], 10);
  const list = text.slice(countMatch.index + countMatch[0].length);
  const lines: PlanFileLine[] = [];
  const row = /^\s*(\d{6})\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*$/gm;
  for (const m of list.matchAll(row)) lines.push(planLine(m));
  if (lines.length !== count) {
    throw new PlanFileError(`Total Lines Count says ${count}, the list has ${lines.length} lines.`);
  }
  return {
    block_name: name,
    zone: zone === null ? null : Math.trunc(zone),
    hemisphere: hemi === 'N' || hemi === 'S' ? hemi : null,
    total_survey_km: total,
    trav_bearing_deg: travBearing,
    trav_spacing_m: travSpacing,
    tie_bearing_deg: capture(text, /Tie Bearing:\s+([\d.]+)/),
    tie_spacing_m: capture(text, /Tie Spacing:\s+([\d.]+) m/),
    lines,
  };
}

const ROW = /^\s*(\d{6})\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s*$/;

function planLine(m: RegExpExecArray | RegExpMatchArray): PlanFileLine {
  const number = Number.parseInt(m[1]!, 10);
  const [x1, y1, x2, y2] = [m[2], m[3], m[4], m[5]].map((v) => Number.parseFloat(v!)) as [
    number,
    number,
    number,
    number,
  ];
  const kind = parseLineNumber(number).kind === 'tie' ? 'tie' : 'traverse';
  return { number, kind, x1, y1, x2, y2, length_m: Math.hypot(x2 - x1, y2 - y1) };
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 === 1 ? v[mid]! : (v[mid - 1]! + v[mid]!) / 2;
}

/** Bearing of a line in degrees, 0 to < 180 (a line and its reverse have the same bearing). */
function lineBearing(l: PlanFileLine): number {
  const deg = (Math.atan2(l.x2 - l.x1, l.y2 - l.y1) * 180) / Math.PI;
  const b = ((deg % 180) + 180) % 180;
  return b >= 179.995 ? 0 : b;
}

/**
 * Bearing and spacing of one kind of line: the median bearing, and the median distance, across that bearing,
 * between lines next to each other in number order (boundary ties and short infill lines do not move a median).
 */
export function measureLines(
  lines: readonly PlanFileLine[],
): { readonly bearing_deg: number; readonly spacing_m: number } | null {
  if (lines.length === 0) return null;
  const bearing = median(lines.map(lineBearing))!;
  const rad = (bearing * Math.PI) / 180;
  // unit normal to the line direction (sin b, cos b)
  const nx = Math.cos(rad);
  const ny = -Math.sin(rad);
  const sorted = [...lines].sort((a, b) => a.number - b.number);
  const gaps: number[] = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const a = sorted[i - 1]!;
    const b = sorted[i]!;
    const gap = Math.abs((b.x1 - a.x1) * nx + (b.y1 - a.y1) * ny);
    if (gap > 0.5) gaps.push(gap);
  }
  const spacing = median(gaps) ?? 0;
  return { bearing_deg: Math.round(bearing * 100) / 100, spacing_m: Math.round(spacing * 10) / 10 };
}

/** Parse a block's `*_LNS.lns` lines file (decision 169: quote km come from it). */
export function parseLnsFile(text: string): LinesFile {
  const rows = text.split(/\r?\n/);
  const header = rows.find((r) => r.trim() !== '') ?? '';
  const h = /^\s*\/\/\s*(.*?)\s+Zone=(\d+)\s+Hemi=([NS])\s*$/.exec(header);
  const lines: PlanFileLine[] = [];
  rows.forEach((r, i) => {
    const t = r.trim();
    if (t === '' || t.startsWith('//')) return;
    const m = ROW.exec(r);
    if (!m) throw new PlanFileError(`Line ${i + 1} of the lines file is not "number x1 y1 x2 y2": "${t}".`);
    lines.push(planLine(m));
  });
  if (lines.length === 0) {
    throw new PlanFileError('This is not a lines file: there is no "number x1 y1 x2 y2" line in it.');
  }
  const trav = measureLines(lines.filter((l) => l.kind === 'traverse'));
  const tie = measureLines(lines.filter((l) => l.kind === 'tie'));
  return {
    block_name: h?.[1] ?? '',
    zone: h ? Number.parseInt(h[2]!, 10) : null,
    hemisphere: h ? (h[3] as 'N' | 'S') : null,
    trav_bearing_deg: trav?.bearing_deg ?? 0,
    trav_spacing_m: trav?.spacing_m ?? 0,
    tie_bearing_deg: tie?.bearing_deg ?? null,
    tie_spacing_m: tie && tie.spacing_m > 0 ? tie.spacing_m : null,
    lines,
  };
}

/** The folder holding a block's flight plan inside its block folder (Thomson's project folder, decision 189). */
export const FLIGHT_PLAN_DIR = '1. Flight Plan';

/** One block's flight-plan files found by the Thomson folder convention (decision 189). */
export interface BlockPlanFiles<F> {
  /** The block folder, `<job>_<block>` (e.g. `F26048_Milgun_patch`); '' when the `1. Flight Plan` folder itself was chosen. */
  readonly folder: string;
  /** The block's lines file: its `*_LNS.lns`, else its `*_fnl.sum` (the same line list); null when neither is there. */
  readonly lines: F | null;
  /** The block's `*_BND.inp`, or null. */
  readonly bnd: F | null;
}

const lower = (s: string): string => s.toLowerCase();
const byName = (a: { readonly name: string }, b: { readonly name: string }): number =>
  a.name < b.name ? -1 : a.name > b.name ? 1 : 0;

/** The file whose (lower-case) name passes the first test of `ranks` that any name passes, ties by name; or null. */
function best<F>(
  files: ReadonlyArray<{ readonly file: F; readonly name: string }>,
  ranks: ReadonlyArray<(name: string) => boolean>,
): F | null {
  for (const rank of ranks) {
    const hit = files.filter((f) => rank(lower(f.name))).sort(byName)[0];
    if (hit) return hit.file;
  }
  return null;
}

/**
 * Each block's flight-plan files in a Thomson project folder (decision 189): a block's flight plan is in
 * `<project folder>\<job>_<block>\1. Flight Plan\` and holds `<job>_<block>_fnl_LNS.lns`, `<job>_<block>_fnl.sum` and
 * the `_BND` files. `pathOf` gives each file's path inside the chosen folder (`/` or `\` separated, e.g. a browser
 * folder pick's `webkitRelativePath`); the project folder, one block folder or the `1. Flight Plan` folder itself may
 * be chosen. Only files directly in a `1. Flight Plan` folder count (not `2. Flights`, not sub-folders).
 *
 * The lines file is the block's `_LNS.lns` (`<folder>_fnl_LNS.lns` first, then any `*_fnl_LNS.lns`, then any
 * `*_LNS.lns`); only when there is none, its `_fnl.sum`. Blocks are in folder-name order; a block without a lines file
 * has `lines: null`.
 */
export function findBlockPlanFiles<F>(files: readonly F[], pathOf: (f: F) => string): BlockPlanFiles<F>[] {
  const byFolder = new Map<string, Array<{ readonly file: F; readonly name: string }>>();
  for (const file of files) {
    const parts = pathOf(file).split(/[\\/]/).filter(Boolean);
    const i = parts.length - 2;
    if (i < 0 || lower(parts[i]!) !== lower(FLIGHT_PLAN_DIR)) continue;
    const folder = i >= 1 ? parts[i - 1]! : '';
    const list = byFolder.get(folder) ?? [];
    list.push({ file, name: parts[i + 1]! });
    byFolder.set(folder, list);
  }
  return [...byFolder.keys()]
    .sort((a, b) => byName({ name: a }, { name: b }))
    .map((folder) => {
      const list = byFolder.get(folder)!;
      const own = lower(folder);
      const lines =
        best(list, [
          (n) => own !== '' && n === `${own}_fnl_lns.lns`,
          (n) => n.endsWith('_fnl_lns.lns'),
          (n) => n.endsWith('_lns.lns'),
        ]) ?? best(list, [(n) => own !== '' && n === `${own}_fnl.sum`, (n) => n.endsWith('_fnl.sum')]);
      const bnd = best(list, [
        (n) => own !== '' && n === `${own}_fnl_bnd.inp`,
        (n) => n.endsWith('_bnd.inp'),
      ]);
      return { folder, lines, bnd };
    });
}

/** An area's name from its block folder or lines file: `F26048_Milgun_patch` (or `…_fnl_LNS.lns`) → `Milgun patch`. */
export function blockNameOf(folderOrFile: string): string {
  return folderOrFile
    .replace(/(_fnl)?(_LNS)?\.(lns|sum)$/i, '')
    .replace(/_fnl$/i, '')
    .replace(/^[A-Z]\d{5}_/i, '')
    .replaceAll('_', ' ');
}

export interface KmlBoundary {
  readonly name: string;
  /** [longitude, latitude] in decimal degrees, WGS 84 (the KML datum), as the file gives them. */
  readonly points: ReadonlyArray<readonly [number, number]>;
}

/**
 * The boundary of a `*_BND.kml` as supplied by the client (decision 170): every `<coordinates>` list in order, joined
 * where one segment starts at the end of the last (the WA files draw the boundary as one LineString per side), with
 * the closing point dropped.
 */
export function parseBoundaryKml(text: string): KmlBoundary {
  const name = /<name>\s*([^<]*?)\s*<\/name>/.exec(text)?.[1] ?? '';
  const points: Array<readonly [number, number]> = [];
  for (const m of text.matchAll(/<coordinates>([\s\S]*?)<\/coordinates>/g)) {
    for (const tuple of m[1]!.trim().split(/\s+/)) {
      if (!tuple) continue;
      const [lng, lat] = tuple.split(',').map(Number);
      if (!Number.isFinite(lng) || !Number.isFinite(lat) || Math.abs(lat!) > 90 || Math.abs(lng!) > 180) {
        throw new PlanFileError(`"${tuple}" in the KML is not "longitude,latitude".`);
      }
      const last = points[points.length - 1];
      if (!last || last[0] !== lng || last[1] !== lat) points.push([lng!, lat!]);
    }
  }
  if (points.length < 3) throw new PlanFileError('The KML has no boundary: fewer than 3 coordinates.');
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (first[0] === last[0] && first[1] === last[1]) points.pop();
  return { name, points };
}

/** Parse a `*_BND.inp` boundary file. */
export function parseBndFile(text: string): BndFile {
  const rows = text.split(/\r?\n/).map((r) => r.trim());
  const zoneNum = Number.parseInt(rows[0] ?? '', 10);
  const n = Number.parseInt(rows[1] ?? '', 10);
  if (!Number.isInteger(n) || n < 0)
    throw new PlanFileError('Line 2 of a boundary file must be the vertex count.');
  if (rows.length < 2 + n)
    throw new PlanFileError(`${n} vertices declared, the file has ${rows.length} lines.`);
  const boundary: Array<readonly [number, number]> = [];
  for (let i = 0; i < n; i += 1) {
    const raw = rows[2 + i]!;
    const xy = raw.replaceAll(',', ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(Number);
    if (xy.length !== 2 || xy.some((v) => !Number.isFinite(v))) {
      throw new PlanFileError(`Vertex ${i + 1} "${raw}" is not "x, y".`);
    }
    boundary.push([xy[0]!, xy[1]!]);
  }
  const after = (k: number): number | null => {
    const v = Number.parseFloat(rows[2 + n + k] ?? '');
    return Number.isFinite(v) ? v : null;
  };
  return {
    zone: Number.isInteger(zoneNum) ? zoneNum : null,
    boundary,
    trav_bearing_deg: after(0),
    trav_spacing_m: after(1),
  };
}
